import { PasskeySettings } from '@valiant/ui-shared'
import { environment } from '../environment'
import { useAuth } from '../AuthProvider'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useGetUserQuery,
  UserRole,
  useUpdateUserMutation,
  useResetPasswordMutation,
  useResetMfaMutation,
} from '@/generated/graphql'
import { useMemo, useState } from 'react'

import { toast } from 'sonner'
import {
  LoadingPage,
  PermissionDrawer,
  UserIcon,
  UserRoleBadge,
  UserStatusBadge,
} from '@valiant/ui-shared'
import { UserSettingsPage, type SettingsUserData } from '@valiant/ui-shared'
import { MainLayout } from '@/components/layout/MainLayout'

const ROLE_SWITCH_OPTIONS: Partial<Record<UserRole, UserRole[]>> = {
  [UserRole.GroupOwner]: [UserRole.GroupOwner, UserRole.Trader],
  [UserRole.Trader]: [UserRole.GroupOwner, UserRole.Trader],
  [UserRole.SuperAdmin]: [UserRole.SuperAdmin, UserRole.Compliance],
  [UserRole.Compliance]: [UserRole.SuperAdmin, UserRole.Compliance],
}

export function UserSettings() {
  const { auth } = useAuth()
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [{ data: userData, fetching: userLoading, error: userError }] = useGetUserQuery({
    variables: { userId: userId! },
    pause: !userId,
  })
  const [{ fetching: updateLoading }, updateUser] = useUpdateUserMutation()
  const [{ fetching: resetPasswordLoading }, resetPassword] = useResetPasswordMutation()
  const [{ fetching: resetMfaLoading }, resetMfa] = useResetMfaMutation()

  // Permissions drawer state
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false)

  const user = userData?.getUser

  // Helper function to get the correct route based on user role
  const getRoleBasedRoute = (role: UserRole, userId: string) => {
    switch (role) {
      case UserRole.SuperAdmin:
        return `/super-admin/${userId}`
      case UserRole.GroupOwner:
        return `/group-owner/${userId}`
      case UserRole.Compliance:
        return `/compliance-officer/${userId}`
      case UserRole.Trader:
      default:
        return `/users/${userId}`
    }
  }

  const handleSave = async (formData: SettingsUserData) => {
    if (!user) return

    try {
      const result = await updateUser({
        input: {
          userId: user.id,
          username: formData.username !== user.username ? formData.username : undefined,
          email: formData.email !== user.email ? formData.email : undefined,
          role: formData.role !== user.role ? (formData.role as UserRole) : undefined,
          isActive: formData.isActive !== user.isActive ? formData.isActive : undefined,
          isWhitelisted:
            formData.isWhitelisted !== user.isWhitelisted ? formData.isWhitelisted : undefined,
          emailNotifications: formData.emailNotifications,
        },
      })

      if (result.error) {
        toast.error('Failed to update user: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('User updated successfully')

        // Get the correct route for the updated role
        const newRoleRoute = getRoleBasedRoute(formData.role as UserRole, user.id)

        // Add a delay to let cache invalidation complete before navigation
        setTimeout(() => {
          navigate(newRoleRoute, { replace: true, state: { flash: 'Saved!' } })
        }, 200)
      }
    } catch (_error) {
      toast.error('Failed to update user')
      throw _error
    }
  }

  const handleResetPassword = async (userId: string) => {
    try {
      const result = await resetPassword({ userId })
      if (result.error) {
        toast.error('Failed to reset password: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('Password reset successfully')
      }
    } catch (_error) {
      toast.error('Failed to reset password')
      throw _error
    }
  }

  const handleResetMfa = async (userId: string) => {
    try {
      const result = await resetMfa({ userId })
      if (result.error) {
        toast.error('Failed to reset MFA: ' + result.error.message)
        throw new Error(result.error.message)
      } else {
        toast.success('MFA reset successfully')
      }
    } catch (_error) {
      toast.error('Failed to reset MFA')
      throw _error
    }
  }

  const handleViewPermissions = () => {
    setIsPermissionsDrawerOpen(true)
  }

  const badges = useMemo(() => {
    if (!user) return []
    return [
      {
        node: <UserRoleBadge role={user.role} />,
        direction: 'LEFT',
      },
      {
        node: (
          <UserStatusBadge
            isWhitelisted={user.isWhitelisted}
            isTrader={user.role === UserRole.Trader}
            isActive={user.isActive}
          />
        ),
        direction: 'LEFT',
      },
    ]
  }, [user])

  if (userLoading) {
    return <LoadingPage />
  }

  if (userError || !user) {
    return (
      <MainLayout>
        <div>User not found</div>
      </MainLayout>
    )
  }

  const settingsUserData: SettingsUserData = {
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    isWhitelisted: user.isWhitelisted,
    emailNotifications: user.emailNotifications || false,
  }

  const allowedRoles = ROLE_SWITCH_OPTIONS[user.role as UserRole]
  const canSwitchRole = allowedRoles != null

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: user.username,
          icon: <UserIcon />,
          hideImage: true,
          badges: badges,
        },
      ]}
      modalsComponent={
        <PermissionDrawer
          open={isPermissionsDrawerOpen}
          onOpenChange={(open) => setIsPermissionsDrawerOpen(open)}
          showAllRoles={true}
        />
      }
      title="User Settings"
    >
      <UserSettingsPage
        user={settingsUserData}
        loading={userLoading}
        showRoleSelector={canSwitchRole}
        allowedRoles={allowedRoles}
        showPermissionsButton={true}
        showPasswordReset={true}
        showMfaReset={true}
        showStatusToggles={true}
        showEmailNotifications={true}
        onSave={handleSave}
        onResetPassword={handleResetPassword}
        onResetMfa={handleResetMfa}
        onViewPermissions={handleViewPermissions}
        saveLoading={updateLoading}
        passwordResetLoading={resetPasswordLoading}
        mfaResetLoading={resetMfaLoading}
      />
      {auth?.role === 'SUPER_ADMIN' && <PasskeySettings apiUrl={environment.API_URL} token={auth.token} admin targetUserId={auth.userId === user.id ? undefined : user.id} />}
    </MainLayout>
  )
}
